import { Badge } from "@ui/badge"
import { Card, CardContent } from "@ui/card"
import {
  AlertTriangle,
  Ban,
  Bot,
  Bug,
  Calendar,
  CheckCircle,
  Clock,
  Hand,
  MailX,
  MessageCircle,
  MessageSquare,
  Repeat,
  ScrollText,
  Send,
  ShieldAlert,
  ShieldOff,
  User,
  Workflow,
  XCircle,
} from "lucide-react"

import type { TimelineEvent, TimelineEventType } from "../types/conversation-timeline"

const EVENT_CONFIG: Record<
  TimelineEventType,
  { icon: typeof MessageCircle; color: string; bgColor: string; label: string }
> = {
  message_inbound: {
    icon: MessageCircle,
    color: "text-blue-600",
    bgColor: "bg-blue-50",
    label: "Mensagem recebida",
  },
  message_outbound: {
    icon: Send,
    color: "text-green-600",
    bgColor: "bg-green-50",
    label: "Mensagem enviada",
  },
  handoff_started: {
    icon: Hand,
    color: "text-orange-600",
    bgColor: "bg-orange-50",
    label: "Handoff iniciado",
  },
  handoff_ended: {
    icon: CheckCircle,
    color: "text-teal-600",
    bgColor: "bg-teal-50",
    label: "Handoff encerrado",
  },
  contact_blocked: {
    icon: Ban,
    color: "text-red-600",
    bgColor: "bg-red-50",
    label: "Contato bloqueado",
  },
  contact_unblocked: {
    icon: ShieldOff,
    color: "text-emerald-600",
    bgColor: "bg-emerald-50",
    label: "Contato desbloqueado",
  },
  appointment_created: {
    icon: Calendar,
    color: "text-purple-600",
    bgColor: "bg-purple-50",
    label: "Agendamento criado",
  },
  appointment_updated: {
    icon: Calendar,
    color: "text-purple-600",
    bgColor: "bg-purple-50",
    label: "Agendamento atualizado",
  },
  appointment_confirmed: {
    icon: CheckCircle,
    color: "text-green-600",
    bgColor: "bg-green-50",
    label: "Agendamento confirmado",
  },
  appointment_cancelled: {
    icon: XCircle,
    color: "text-red-600",
    bgColor: "bg-red-50",
    label: "Agendamento cancelado",
  },
  appointment_completed: {
    icon: CheckCircle,
    color: "text-emerald-600",
    bgColor: "bg-emerald-50",
    label: "Agendamento concluído",
  },
  appointment_no_show: {
    icon: User,
    color: "text-amber-600",
    bgColor: "bg-amber-50",
    label: "No-show",
  },
  scheduled_message: {
    icon: Clock,
    color: "text-indigo-600",
    bgColor: "bg-indigo-50",
    label: "Mensagem agendada",
  },
  automation_executed: {
    icon: Workflow,
    color: "text-teal-600",
    bgColor: "bg-teal-50",
    label: "Automação executada",
  },
  conversation_summarized: {
    icon: ScrollText,
    color: "text-blue-600",
    bgColor: "bg-blue-50",
    label: "Conversa resumida",
  },
  tool_execution_failed: {
    icon: AlertTriangle,
    color: "text-rose-600",
    bgColor: "bg-rose-50",
    label: "Falha em ferramenta",
  },
  tool_iteration_cap: {
    icon: Repeat,
    color: "text-orange-600",
    bgColor: "bg-orange-50",
    label: "Limite de iterações",
  },
  guardrail_blocked: {
    icon: ShieldAlert,
    color: "text-rose-600",
    bgColor: "bg-rose-50",
    label: "Bloqueado por guardrail",
  },
  agent_run_failed: {
    icon: Bug,
    color: "text-rose-600",
    bgColor: "bg-rose-50",
    label: "Falha no agente",
  },
  message_delivery_failed: {
    icon: MailX,
    color: "text-amber-600",
    bgColor: "bg-amber-50",
    label: "Falha na entrega",
  },
}

function formatTimestamp(ts: string) {
  const date = new Date(ts)
  return date.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  })
}

export function TimelineEventCard({ event }: { event: TimelineEvent }) {
  const config = EVENT_CONFIG[event.event_type]
  const Icon = config.icon
  const details = event.details

  return (
    <div className="flex gap-3">
      <div
        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${config.bgColor}`}
      >
        <Icon className={`h-4 w-4 ${config.color}`} />
      </div>

      <Card className="flex-1 shadow-none">
        <CardContent className="p-3">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <div className="mb-1 flex flex-wrap items-center gap-2">
                <span className="text-muted-foreground text-xs font-medium">{config.label}</span>
                {renderBadges(event)}
              </div>
              <p className="text-sm break-words">{event.summary}</p>
              {renderDetails(event, details)}
            </div>
            <span className="text-muted-foreground shrink-0 text-xs">
              {formatTimestamp(event.timestamp)}
            </span>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

function renderBadges(event: TimelineEvent) {
  const badges: React.ReactNode[] = []
  const details = event.details

  if (event.event_type === "message_outbound" && details.is_ai_message) {
    badges.push(
      <Badge key="ai" variant="secondary" className="text-xs">
        <Bot className="mr-1 h-3 w-3" />
        IA
      </Badge>
    )
  }

  if (event.event_type === "message_outbound" && !details.is_ai_message) {
    badges.push(
      <Badge key="human" variant="outline" className="text-xs">
        <MessageSquare className="mr-1 h-3 w-3" />
        Humano
      </Badge>
    )
  }

  if (details.status) {
    badges.push(
      <Badge key="status" variant="outline" className="text-xs">
        {String(details.status)}
      </Badge>
    )
  }

  return badges
}

function renderDetails(event: TimelineEvent, details: Record<string, unknown>) {
  if (
    event.event_type === "appointment_created" ||
    event.event_type === "appointment_confirmed" ||
    event.event_type === "appointment_cancelled" ||
    event.event_type === "appointment_completed" ||
    event.event_type === "appointment_no_show"
  ) {
    return (
      <div className="text-muted-foreground mt-1 space-y-0.5 text-xs">
        {!!details.start_time && <p>Início: {formatTimestamp(String(details.start_time))}</p>}
        {!!details.client_name && <p>Cliente: {String(details.client_name)}</p>}
        {!!details.provider_name && <p>Profissional: {String(details.provider_name)}</p>}
        {!!details.notes && <p>Notas: {String(details.notes)}</p>}
      </div>
    )
  }

  if (event.event_type === "handoff_started" || event.event_type === "handoff_ended") {
    return (
      <div className="text-muted-foreground mt-1 space-y-0.5 text-xs">
        {!!details.reason && <p>Razão: {String(details.reason)}</p>}
        {!!details.handoff_until_date && (
          <p>Até: {formatTimestamp(String(details.handoff_until_date))}</p>
        )}
        {!!details.source && <p>Fonte: {String(details.source)}</p>}
      </div>
    )
  }

  if (event.event_type === "scheduled_message") {
    return (
      <div className="text-muted-foreground mt-1 space-y-0.5 text-xs">
        {!!details.type && <p>Tipo: {String(details.type)}</p>}
        {!!details.scheduled_time && (
          <p>Agendado para: {formatTimestamp(String(details.scheduled_time))}</p>
        )}
        {!!details.error_reason && (
          <p className="text-red-500">Erro: {String(details.error_reason)}</p>
        )}
      </div>
    )
  }

  return null
}
